import { ICertification, IUser, IUserBadge } from '@/types';
import { useAppLoading, useViewScoreCard } from '@/hooks';
import { useCurrentUserFeature } from '../users';
import { useEffect } from 'react';
import { useGlobalStore, useCurrentUser } from '@/store';

interface UseSalesRepDashboardFeatureReturn {
  /** List of certifications for the current user */
  certifications: ICertification[];
  /** Error object for certifications fetch */
  certificationsError: { message: string; status: number } | null;
  /** Current user object */
  currentUser: IUser | null;
  /** List of badges for the current user */
  currentUserBadges: IUserBadge[];
  /** First name of the current user for personalized greetings */
  currentUserFirstName: string;
  /** Full name of the current user for profile display */
  currentUserFullName: string;
  /** Callback to handle viewing a scorecard */
  handleViewScoreCardClick: (scoreId: string) => void;
  /** Loading state for the dashboard */
  isLoading: boolean;
}

/**
 * Feature hook that encapsulates sales-rep dashboard orchestration logic.
 *
 * Responsibilities:
 * - Gather dashboard-level store values.
 * - Derive current-user display names.
 * - Reset active global selections on mount.
 * - Provide scorecard view callback scoped to current user.
 */
export const useSalesRepDashboardFeature = (): UseSalesRepDashboardFeatureReturn => {
  // ================================
  // Loading and global store state
  // ================================
  const isLoading = useAppLoading();
  const { certifications, certificationsError, currentUserBadges, resetActives } = useGlobalStore();

  // ================================
  // Current user derived values
  // ================================
  const currentUser = useCurrentUser();
  const { currentUserFirstName, currentUserFullName } = useCurrentUserFeature({ currentUser });

  // ================================
  // Mount lifecycle behavior
  // ================================
  useEffect(() => {
    resetActives();
  }, [resetActives]);

  // ================================
  // Scorecard callback
  // ================================
  const { handleViewScoreCardClick } = useViewScoreCard({ userName: currentUserFullName });

  // ================================
  // Return values
  // ================================
  return {
    certifications,
    certificationsError,
    currentUser,
    currentUserBadges,
    currentUserFirstName,
    currentUserFullName,
    handleViewScoreCardClick,
    isLoading,
  };
};
